/**
 * Mermaid — inline diagram with click-to-expand full-screen viewer.
 *
 * Follows the taiga-app pattern:
 * - Compact inline preview with themed styling
 * - Hover overlay with "Expand" button
 * - Click opens DiagramViewer modal with zoom/pan/pinch
 * - Lazy-loads mermaid on mount (code-split by Vite)
 * - Singleton init (only re-inits on theme change)
 * - Taiga-branded theme (Slate palette, not generic mermaid themes)
 */

import { useEffect, useId, useMemo, useState } from "react";
import { AlertCircle, Maximize2 } from "lucide-react";
import { Button, cn } from "@taiga-platform/ui";
import DOMPurify from "dompurify";
import { useTheme } from "../../lib/theme";
import { DiagramViewer } from "./DiagramViewer";
import type { MermaidConfig } from "mermaid";

// -- Theme config (from taiga-app's mermaid-theme.ts) --

const lightThemeVariables = {
  primaryColor: "#F1F5F9",
  primaryTextColor: "#334155",
  primaryBorderColor: "#CBD5E1",
  secondaryColor: "#F8FAFC",
  tertiaryColor: "#F1F5F9",
  lineColor: "#CBD5E1",
  fontFamily: "Inter, system-ui, sans-serif",
  fontSize: "12px",
  edgeLabelBackground: "#F8FAFC",
} as const;

const darkThemeVariables = {
  primaryColor: "#1E293B",
  primaryTextColor: "#94A3B8",
  primaryBorderColor: "#334155",
  secondaryColor: "#0F172A",
  tertiaryColor: "#1E293B",
  lineColor: "#475569",
  fontFamily: "Inter, system-ui, sans-serif",
  fontSize: "12px",
  edgeLabelBackground: "#1E293B",
} as const;

function getMermaidConfig(theme: "light" | "dark"): MermaidConfig {
  return {
    startOnLoad: false,
    theme: "base",
    securityLevel: "strict",
    themeVariables: {
      ...(theme === "light" ? lightThemeVariables : darkThemeVariables),
      background: "transparent",
    },
    flowchart: {
      useMaxWidth: false,
      htmlLabels: true,
      curve: "linear",
      padding: 16,
      nodeSpacing: 30,
      rankSpacing: 60,
      diagramPadding: 8,
    },
  };
}

// -- Singleton init --

let currentTheme: "light" | "dark" | null = null;

async function initMermaid(theme: "light" | "dark") {
  if (currentTheme === theme) return;
  currentTheme = theme;
  const { default: mermaid } = await import("mermaid");
  mermaid.initialize(getMermaidConfig(theme));
}

// -- Component --

interface MermaidProps {
  chart: string;
  label?: string;
  className?: string;
}

export function Mermaid({ chart, label, className }: MermaidProps) {
  const { resolved } = useTheme();
  const id = useId().replace(/:/g, "-");
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const sanitizedSvg = useMemo(
    () =>
      svg
        ? DOMPurify.sanitize(svg, {
            USE_PROFILES: { svg: true, svgFilters: true },
            ADD_TAGS: ["foreignObject"],
          })
        : null,
    [svg],
  );

  useEffect(() => {
    if (!chart) return;
    let cancelled = false;
    setSvg(null);
    setError(null);

    (async () => {
      try {
        await initMermaid(resolved);
        const { default: mermaid } = await import("mermaid");
        const renderId = `mermaid-${id}-${Date.now()}`;
        const { svg: rendered } = await mermaid.render(
          renderId,
          chart.trim(),
        );
        if (!cancelled) setSvg(rendered);
      } catch (e) {
        if (!cancelled) {
          console.error("Mermaid render error:", e);
          setError(
            e instanceof Error ? e.message : "Failed to render diagram",
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [chart, id, resolved]);

  if (error) {
    return (
      <div
        className={cn(
          "not-prose flex items-center gap-2 rounded-lg bg-destructive/10 p-4 text-sm text-destructive",
          className,
        )}
      >
        <AlertCircle className="size-4 shrink-0" />
        <span>Failed to render diagram: {error}</span>
      </div>
    );
  }

  if (!svg) {
    return (
      <div
        className={cn(
          "not-prose my-6 animate-pulse rounded-lg bg-muted h-32",
          className,
        )}
      />
    );
  }

  const diagramTitle = label || "Diagram";

  return (
    <>
      {/* Inline preview with hover-to-expand */}
      <div
        className={cn(
          "not-prose group relative my-6 overflow-hidden rounded-lg border bg-card",
          "hover:border-primary/50 transition-colors",
          className,
        )}
      >
        <div className="max-h-[400px] overflow-hidden p-4">
          <div
            className="flex justify-center [&_svg]:max-h-[360px] [&_svg]:w-auto [&_svg]:mx-auto"
            // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
            dangerouslySetInnerHTML={{ __html: sanitizedSvg! }}
          />
        </div>

        {/* Expand overlay — appears on hover */}
        <div
          className={cn(
            "absolute inset-0 flex items-center justify-center",
            "bg-background/60 backdrop-blur-[2px] opacity-0 group-hover:opacity-100",
            "transition-opacity duration-200",
          )}
        >
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setExpanded(true)}
            className="gap-2 shadow-md"
          >
            <Maximize2 className="size-4" />
            Expand
          </Button>
        </div>

        {/* Touch-friendly click target for mobile */}
        <button
          onClick={() => setExpanded(true)}
          className="absolute inset-0 lg:hidden"
          aria-label="Click to expand diagram"
        />
      </div>

      {/* Full-screen viewer modal */}
      <DiagramViewer
        title={diagramTitle}
        description="Full-screen view with zoom and pan"
        svgHtml={svg}
        open={expanded}
        onOpenChange={setExpanded}
      />
    </>
  );
}
